<template>
  <div ref="rootEl" class="pycad-rotate-popover">
    <button
      ref="triggerRef"
      type="button"
      class="pycad-tool-btn pycad-tool-btn--labeled pycad-rotate-popover__trigger"
      :class="{
        'pycad-tool-btn--active': open,
        'pycad-tool-btn--disabled': disabled,
      }"
      :disabled="disabled"
      @click="toggleOpen"
    >
      <i class="mdi mdi-rotate-right" :style="{ fontSize: '17px' }"></i>
      <span class="pycad-tool-btn__caption pycad-rotate-popover__caption">
        Rotate
        <i
          class="mdi mdi-chevron-down pycad-rotate-popover__chevron"
          :class="{ 'pycad-rotate-popover__chevron--open': open }"
        ></i>
      </span>
    </button>

    <Teleport to="body">
      <transition name="pycad-dropdown-fade">
        <div
          v-if="open"
          ref="panelRef"
          class="pycad-rotate-popover__panel"
          :style="menuStyle"
        >
          <div class="pycad-rotate-popover__title">View rotation</div>

          <div class="pycad-rotate-popover__value-row">
            <span class="pycad-rotate-popover__value-label">Angle</span>
            <span class="pycad-rotate-popover__value">{{ displayAngle }}°</span>
          </div>

          <input
            type="range"
            class="pycad-rotate-popover__slider"
            min="0"
            max="360"
            step="1"
            :value="sliderAngle"
            :disabled="disabled || !viewId"
            @input="onSliderInput"
          />

          <div class="pycad-rotate-popover__presets">
            <button
              v-for="preset in presets"
              :key="preset"
              type="button"
              class="pycad-rotate-popover__preset"
              :class="{
                'pycad-rotate-popover__preset--active': displayAngle === preset,
              }"
              :disabled="disabled || !viewId"
              @click="setAngle(preset)"
            >
              {{ preset }}°
            </button>
          </div>

          <button
            type="button"
            class="pycad-rotate-popover__reset"
            :disabled="disabled || !viewId || displayAngle === 0"
            @click="setAngle(0)"
          >
            Reset to 0°
          </button>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import {
  getViewRotation,
  triggerCameraOp,
  viewRotationDegrees,
} from '@/src/core/cameraOps';
import { useFixedDropdownMenu } from '@/src/composables/useFixedDropdownMenu';

interface Props {
  viewId?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  viewId: undefined,
  disabled: false,
});

const open = ref(false);
const rootEl = ref<HTMLElement>();
const triggerRef = ref<HTMLElement>();
const panelRef = ref<HTMLElement>();
const sliderAngle = ref(0);

const { menuStyle } = useFixedDropdownMenu(open, triggerRef);

onClickOutside(
  rootEl,
  () => {
    open.value = false;
  },
  { ignore: [panelRef] }
);

const presets = [0, 90, 180, 270];

const displayAngle = computed(() => Math.round(sliderAngle.value));

watch(
  () => (props.viewId ? (viewRotationDegrees[props.viewId] ?? 0) : 0),
  (angle) => {
    sliderAngle.value = angle;
  },
  { immediate: true }
);

function toggleOpen() {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value && props.viewId) {
    sliderAngle.value = getViewRotation(props.viewId);
  }
}

function setAngle(angle: number) {
  if (!props.viewId || props.disabled) return;
  sliderAngle.value = angle;
  triggerCameraOp({ viewId: props.viewId, op: 'setRotation', angle });
}

function onSliderInput(event: Event) {
  const value = Number((event.target as HTMLInputElement).value);
  setAngle(value);
}
</script>

<style scoped>
.pycad-rotate-popover {
  position: relative;
  display: inline-flex;
}

.pycad-rotate-popover__caption {
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

.pycad-rotate-popover__chevron {
  font-size: 11px;
  opacity: 0.7;
  transition: transform 0.15s ease;
}

.pycad-rotate-popover__chevron--open {
  transform: rotate(180deg);
}

.pycad-rotate-popover__panel {
  width: 220px;
  padding: 12px;
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.55);
}

.pycad-rotate-popover__title {
  margin-bottom: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--pycad-text-muted, #666);
}

.pycad-rotate-popover__value-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 12px;
}

.pycad-rotate-popover__value-label {
  color: var(--pycad-text-secondary, #b5b5b5);
  font-weight: 500;
}

.pycad-rotate-popover__value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: var(--pycad-primary, #ff9900);
}

.pycad-rotate-popover__slider {
  width: 100%;
  accent-color: var(--pycad-primary, #ff9900);
  cursor: pointer;
}

.pycad-rotate-popover__slider:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pycad-rotate-popover__presets {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  margin-top: 10px;
}

.pycad-rotate-popover__preset {
  padding: 5px 0;
  font-size: 11px;
  font-weight: 600;
  font-family: 'JetBrains Mono', monospace;
  color: var(--pycad-text-secondary, #b5b5b5);
  background: transparent;
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius-sm, 6px);
  cursor: pointer;
  transition:
    color 0.12s ease,
    border-color 0.12s ease,
    background 0.12s ease;
}

.pycad-rotate-popover__preset:hover:not(:disabled) {
  color: var(--pycad-text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.18);
}

.pycad-rotate-popover__preset--active {
  color: var(--pycad-primary, #ff9900);
  border-color: rgba(255, 153, 0, 0.45);
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.12));
}

.pycad-rotate-popover__preset:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pycad-rotate-popover__reset {
  width: 100%;
  margin-top: 10px;
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  color: var(--pycad-text-muted, #666);
  background: transparent;
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius-sm, 6px);
  cursor: pointer;
  transition:
    color 0.12s ease,
    border-color 0.12s ease;
}

.pycad-rotate-popover__reset:hover:not(:disabled) {
  color: var(--pycad-primary, #ff9900);
  border-color: rgba(255, 153, 0, 0.4);
}

.pycad-rotate-popover__reset:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pycad-dropdown-fade-enter-active,
.pycad-dropdown-fade-leave-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}

.pycad-dropdown-fade-enter-from,
.pycad-dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
